import { onAnimationEnd } from '@theme/utilities';

// Create a new custom element for product links with images for transitions to PDP
class ProductCardLink extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => setTimeout(() => this.#handleClick(event), 0));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for the product link
   * @param {Event} event
   */
  #handleClick = (event) => {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // R-CARDLINK: only a click that leaves the page may prepare the transition to the product page.
    // This listener hears EVERY click inside the card, and the old check only excluded inputs, so the
    // card's own ADD TO CART button ran it too. Measured on the Build-a-Box collection, dev theme: one
    // add-to-cart tap replaced the first card image's srcset (300-800w, showing 800w) with a single
    // `width=500` URL carrying no descriptors, downloaded that file again (37,008 B), and left
    // `data-view-transition-triggered="true"` on a gallery that never navigated. Every card on
    // Build-a-Box, the collection grid and /search carries the 500w URL.
    // The list mirrors `navigateToProduct` (`assets/product-card.js:349`), which already declines to
    // navigate for exactly these controls. Anchors are deliberately NOT in it: the product card's
    // overlay link and `resource-card`'s `.resource-card__link` both navigate, and a click on them
    // must still get the transition.
    if (event.target instanceof Element && event.target.closest('button, input, label, select, [tabindex="1"]')) return;

    const gallery = this.querySelector('[data-view-transition-to-main-product]');
    if (!this.productTransitionEnabled || !(gallery instanceof HTMLElement)) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const activeImage =
      gallery.querySelector('slideshow-slide[aria-hidden="false"] [transitionToProduct="true"]') ||
      gallery.querySelector('[transitionToProduct="true"]:last-child');

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    gallery.setAttribute('data-view-transition-type', 'product-image-transition');
    gallery.setAttribute('data-view-transition-triggered', 'true');
  };

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (this.featuredMediaUrl && !image.srcset.includes(this.featuredMediaUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}
